import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CalendarCheck, Loader2, XCircle, Clock, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_TRIES = 12;

const EventBevestiging = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling");
  const [booking, setBooking] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/events/bookings/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setBooking(data.booking);
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          setState("error");
          return;
        }
      }
      tries.current += 1;
      if (tries.current >= MAX_TRIES) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <section className="min-h-screen bg-ink text-white flex items-center justify-center px-6 py-40">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-xl text-center"
        data-testid="event-confirmation"
      >
        {state === "polling" && (
          <>
            <Loader2 size={30} className="text-gold animate-spin mx-auto" />
            <h1 className="font-serif text-3xl md:text-4xl mt-8">Je betaling wordt bevestigd...</h1>
            <p className="mt-5 text-white/60 text-sm">
              Eén moment — sluit dit venster nog niet.
            </p>
          </>
        )}

        {state === "paid" && booking && (
          <>
            <CalendarCheck size={34} className="text-gold mx-auto" strokeWidth={1.3} />
            <p className="eyebrow text-gold mt-8 mb-3">Geboekt &amp; betaald</p>
            <h1 className="font-serif text-4xl md:text-5xl leading-tight">
              Je plekje is <span className="italic text-gold">vast.</span>
            </h1>
            <p className="mt-6 text-white/70 leading-relaxed">
              Bedankt {booking.name}! Je boeking voor {booking.event_title} is bevestigd. De
              bevestigingsmail met alle details staat in je inbox.
            </p>
            <div className="mt-10 border border-white/10 divide-y divide-white/10 text-left">
              <p className="flex items-center gap-3 px-6 py-4 text-sm">
                <CalendarCheck size={15} className="text-gold" />
                {booking.slot_date}
              </p>
              <p className="flex items-center gap-3 px-6 py-4 text-sm">
                <Clock size={15} className="text-gold" />
                {booking.slot_time}
              </p>
              <p className="flex items-center gap-3 px-6 py-4 text-sm">
                <MapPin size={15} className="text-gold" />
                Details in je bevestigingsmail
              </p>
            </div>
            <Link to="/" className="btn-outline-light mt-10 inline-flex">
              Terug naar de site
            </Link>
          </>
        )}

        {(state === "error" || state === "expired" || state === "timeout") && (
          <>
            <XCircle size={30} className="text-gold mx-auto" strokeWidth={1.3} />
            <h1 className="font-serif text-3xl md:text-4xl mt-8">
              {state === "expired" ? "De betaaltijd is verstreken" : "Nog geen bevestiging"}
            </h1>
            <p className="mt-5 text-white/60 leading-relaxed">
              {state === "expired"
                ? "Je tijdslot werd 15 minuten vrijgehouden en is nu weer beschikbaar. Probeer opnieuw te boeken."
                : "We konden je betaling nog niet bevestigen. Heb je betaald? Dan volgt je bevestigingsmail automatisch. Bij twijfel mag je altijd contacteren."}
            </p>
            <div className="flex flex-wrap gap-4 justify-center mt-10">
              <Link to="/mini-shoots" className="btn-gold" data-testid="event-retry-link">
                Terug naar de mini-shoots
              </Link>
              <Link to="/contact" className="btn-outline-light">
                Contacteer Eric
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </section>
  );
};

export default EventBevestiging;
